import React from 'react';
import { ContractTag } from 'api/useTags';
import { Tag } from 'api/useTags';
import { FormInstance } from 'antd/es/form/Form';
import { message } from 'antd';

/**
 * Sentinel prefix used as a placeholder `Tag.id` for tags typed by the user but not yet persisted.
 * Anything matching `${NEW_TAG_SENTINEL_PREFIX}<n>` in a form `tagIds` value represents an unsaved
 * tag; the matching `Tag` entry in local state carries the chosen name + color until the form is
 * submitted, at which point it is sent to the API in `new_tags[]` instead of `tag_ids[]`.
 */
export const NEW_TAG_SENTINEL_PREFIX = 'newTag:';

/**
 * Lightweight tag shape used by render helpers — covers both saved tags (`tagId`) and
 * Salesforce/contract tags ({@link ContractTag}) without committing to a specific source DTO.
 */
export interface RenderableTag {
  tagId: number | string;
  name: string;
  color?: string | null;
}

/**
 * @param {unknown} id - Tag id from the form value
 * @returns {boolean} True when `id` is a `newTag:` sentinel string
 */
export const isNewTagSentinel = (id: unknown): id is string =>
  typeof id === 'string' && id.startsWith(NEW_TAG_SENTINEL_PREFIX);

/**
 * Generates a fresh `newTag:<n>` sentinel for an unsaved tag entry.
 * @returns {string} New sentinel id
 */
export const buildNewTagSentinel = (): string => `${NEW_TAG_SENTINEL_PREFIX}${Math.floor(Math.random() * 1_000_000)}`;

/**
 * Get tag color from the API response and convert to Tailwind CSS classes
 * @param {string} tagLabel - The tag name to find
 * @param {Tag[]} tags - Array of tags to search through
 * @returns {object} Object containing Tailwind CSS classes for background, text, icon, and close button
 */
export const getSelectedTagColor = (
  tagLabel: string,
  tags: Tag[]
): { bg: string; text: string; icon: string; closeBg: string; bgStyle?: string } => {
  const tag = tags.find((t) => t.name === tagLabel);

  if (tag) {
    // Convert hex color to Tailwind-like classes
    const hexToTailwind = (hex: string) => {
      // Check if the color is a light gray (should use black text)
      const isLightGray = (color: string) => {
        const lightGrayColors = ['#f0f0f0'];
        return lightGrayColors.includes(color.toLowerCase());
      };

      // Simple mapping - you can expand this based on your needs
      switch (hex.toLowerCase()) {
        case '#faad14':
          return { bg: 'bg-yellow-500', text: 'text-yellow-800', icon: 'text-yellow-600', closeBg: 'bg-yellow-100' };
        default: {
          // For light gray colors, use black text; for others, use white text
          const textColor = isLightGray(hex) ? 'text-black' : 'text-white';
          const iconColor = isLightGray(hex) ? 'text-black' : 'text-white';
          const closeBgColor = isLightGray(hex) ? 'bg-black/10' : 'bg-white/20';

          // For most colors, use inline style for background since Tailwind doesn't support arbitrary hex
          return {
            bg: '', // No Tailwind class for arbitrary hex
            text: textColor,
            icon: iconColor,
            closeBg: closeBgColor,
            bgStyle: `background-color: ${hex}` // Use inline style instead
          };
        }
      }
    };
    return hexToTailwind(tag.color || '#f0f0f0');
  }

  // Default fallback
  return { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'text-gray-600', closeBg: 'bg-black/10' };
};

export const Pebble = ({
  color = '#f0f0f0',
  name,
  extra
}: {
  color?: string;
  name: string;
  extra?: number;
}): React.ReactElement => {
  return (
    <span
      className="pl-2 pr-1 py-1 rounded-full text-sm font-medium flex justify-between items-center"
      style={{
        backgroundColor: color,
        color: color === '#f0f0f0' ? '#000000' : '#ffffff'
      }}
    >
      <span className="mr-3">{name}</span>
      {extra && <span className="rounded-full text-sm font-medium px-2 bg-white text-black">+{extra}</span>}
    </span>
  );
};

/**
 * Renders a list of tags as colored pills. Accepts the {@link ContractTag} shape used in contract
 * flows or the lighter {@link RenderableTag} shape used by deal views — anything with a stable id,
 * a name, and an optional color.
 * @param {RenderableTag[]} tags - Array of tags to render
 * @returns {React.ReactElement} JSX element with rendered tags
 */
export const renderSelectedTags = (tags: RenderableTag[] | ContractTag[]): React.ReactElement => {
  if (!tags || tags.length === 0) {
    return <span>--</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((t) => (
        <Pebble key={t.tagId} color={t.color ?? '#f0f0f0'} name={t.name} />
      ))}
    </div>
  );
};

/**
 * Custom hook for handling tag keydown events
 * Adds new tags to the dropdown selection with default color and generated ID
 * @returns {(e: React.KeyboardEvent<HTMLInputElement>, form: FormInstance, profile_id?: number | undefined, fetchTags?: () => Promise<void>, fieldName?: string, setTags?: React.Dispatch<React.SetStateAction<Tag[]>>) => Promise<void>} Function to handle tag keydown events
 */
export const useTagKeyDown = () => {
  const handleTagKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    form: FormInstance,
    profile_id?: number | undefined,
    fetchTags?: () => Promise<void>,
    fieldName = 'tags',
    setTags?: React.Dispatch<React.SetStateAction<Tag[]>>
  ): Promise<void> => {
    if (e.key === 'Enter' && (e.target as HTMLInputElement).value) {
      const newTag = (e.target as HTMLInputElement).value.trim();
      // Enforce max length of 32 characters for new tags
      if (newTag.length > 32) {
        e.preventDefault();
        message.warning('Tag name cannot exceed 32 characters.');
        return;
      }
      if (newTag) {
        try {
          // Sentinel id for an unsaved tag; resolved to a `new_tags[]` payload entry at submit time.
          const randomId = buildNewTagSentinel();

          // Create a temporary tag object
          const tempTag: Tag = {
            id: randomId,
            name: newTag,
            color: '#f0f0f0'
          };

          // Add the tag to the tags array if setTags is provided
          if (setTags) {
            setTags((prevTags) => [...prevTags, tempTag]);
          }

          // Add the tag to the form with the generated ID
          const currentTags = form.getFieldValue(fieldName) || [];
          if (!currentTags.includes(randomId)) {
            form.setFieldValue(fieldName, [...currentTags, randomId]);
          }

          // Clear the input by triggering a blur and focus to reset the internal state
          setTimeout(() => {
            (e.target as HTMLInputElement).blur();
            (e.target as HTMLInputElement).focus();
          }, 0);
        } catch (error) {
          console.error('Error adding tag:', error);
          message.error('Failed to add tag. Please try again.');
        }
      }
    }
  };

  return handleTagKeyDown;
};
